import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { SCENE_KIND_NOUN, sceneKindSchema } from '@platform/schema/content';
import { mechanicSwitches } from '@platform/schema/mechanic';
import { serialList } from '@platform/schema/prose';
import { useState } from 'react';

import { ChoiceField } from './ChoiceField';
import { FIELD_NAMES } from './edits';

import type { AuthorableKind } from '@platform/schema/content';
import type { MechanicSwitch } from '@platform/schema/mechanic';

/**
 * Changing how an existing question is asked — GEN-78.
 *
 * Not saved on selection, unlike every other field on this screen. `autosave-with-undo`
 * chose blur-to-save for *corrections*, a loop of many small fixes where a Save click
 * each is friction people route around. This is not one of those: it rewrites what the
 * scene is and drops the answer key, and no patch can put a null column back — so an
 * explicit button is the honest shape. `docs/decisions/switching-a-mechanic.md`.
 */
export function MechanicPicker({
  kind,
  itemCount,
  disabled,
  onSwitch,
}: {
  kind: string;
  /** How many labelled items the scene holds, which decides what the warning says. */
  itemCount: number;
  disabled: boolean;
  onSwitch: (next: AuthorableKind) => void;
}) {
  // `safeParse` and not `.catch(…)`: the SDK types `kind` as a plain string, and a
  // fallback would render a full picker offering to switch a scene away from a mechanic
  // it does not have. A kind this build does not know is one to show no control for.
  const known = sceneKindSchema.safeParse(kind);

  const options = known.success ? mechanicSwitches(known.data, itemCount) : [];

  const [target, setTarget] = useState<AuthorableKind | undefined>(undefined);

  // A narrative beat has no mechanic, so there is nothing to offer and no empty control
  // to explain away.
  if (options.length === 0) return null;

  const chosen = options.find((one) => one.kind === target);

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <ChoiceField
          label="Change the mechanic"
          value={target}
          options={options.map((option) => option.kind)}
          describe={(option) => SCENE_KIND_NOUN[option]}
          disabled={disabled}
          onChange={setTarget}
        />

        <Button
          variant="outlined"
          disabled={disabled || chosen === undefined}
          onClick={() => {
            if (chosen === undefined) return;

            onSwitch(chosen.kind);
            setTarget(undefined);
          }}
          sx={{ mt: 0.25 }}
        >
          Change
        </Button>
      </Stack>

      {chosen !== undefined && (
        <Alert severity="info">{consequence(chosen, itemCount)}</Alert>
      )}
    </Stack>
  );
}

/**
 * What an obligation is called in a sentence, where `FIELD_NAMES` names a single editor.
 *
 * "Scene 2 Item" is one field and undoing it is one edit; "set Item" is not what a
 * reviewer owes — they owe the key for every item. Only the two that genuinely differ are
 * here, so everything else still reads the one map the undo button and the proposal diff
 * read, and "Total to divide" stays the same words in all three places.
 */
const OWED: Record<string, string> = {
  items: 'the answer key',
  buckets: 'the buckets',
};

/**
 * What this change will do, in the order the reviewer needs it: what happens to the work
 * they have, then what they will owe.
 */
export function consequence(change: MechanicSwitch, itemCount: number): string {
  const noun = SCENE_KIND_NOUN[change.kind];
  const said: string[] = [];

  if (itemCount > 0)
    said.push(
      change.usesItems
        ? `Your ${String(itemCount)} items keep their labels.`
        : `A ${noun} has no items. Your ${String(itemCount)} stay saved and come back if you switch to a mechanic that uses them.`,
    );

  said.push(
    change.needs.length === 0
      ? 'Nothing else to fill in.'
      : `The answer is cleared, so set ${serialList(change.needs.map((field) => OWED[field] ?? FIELD_NAMES[field] ?? field))} before this lesson can publish.`,
  );

  return said.join(' ');
}
